<template>
  <div class="table-ctrls-container">
    <div class="flex-grid">
      <div class="row-class">
        <div class="size-and-select">
          <p>Rows per page:</p>
          <select v-model="selPageSize" class="form-control" aria-label="Rows per page">
            <option>5</option>
            <option>10</option>
            <option>25</option>
            <option>50</option>
            <option>100</option>
            <option>250</option>
            <option>500</option>
            <option>1000</option>
          </select>
        </div>
        <slot />
        <span v-if="numEntriesFiltered">
          <template v-if="isAnyFilterApplied">
            {{ pageSize * page + 1 }} - {{ Math.min(pageSize * (page + 1), numEntriesFiltered) }} of
            {{ numEntriesFiltered }} ({{ numEntries }} total)
          </template>
          <template v-else>
            {{ pageSize * page + 1 }} - {{ Math.min(pageSize * (page + 1), numEntries) }} of
            {{ numEntries }}
          </template>
        </span>
        <a
          v-if="more && !loading"
          @click.stop="$emit('download-more')"
          @keyup.enter="$emit('download-more')"
          >Load more entries...</a
        >
        <a
          v-if="loading"
          aria-label="Loading..."
          @click.stop="$emit('download-cancel')"
          @keyup.enter="$emit('download-cancel')"
          ><LoadingAnim tag="span" inline
        /></a>
      </div>
    </div>
    <div>
      <nav v-if="showPagination" aria-label="Page navigation" class="pull-right pagination-nav">
        <ul class="pagination pagination-ul">
          <li>
            <a aria-label="First page" @click.stop="currentPage = 0" @keyup.enter="currentPage = 0">
              <span aria-hidden="true">First</span>
            </a>
          </li>
          <li :class="{ disabled: !currentPage || !maxPages }">
            <a
              class="pag-icon"
              aria-label="Previous"
              @click.stop="currentPage -= 1"
              @keyup.enter="currentPage -= 1"
            >
              <span aria-hidden="true">
                <i class="fa fa-backward"></i>
              </span>
            </a>
          </li>
          <li v-if="page > pagePadding" class="disabled">
            <a href="#">...</a>
          </li>
          <li
            v-for="idx in numPagesToShow"
            :key="'pager-' + pageIdx(idx)"
            :class="{ active: pageIdx(idx) === page }"
          >
            <a
              class="pag-number"
              @click.stop="currentPage = pageIdx(idx)"
              @keyup.enter="currentPage = pageIdx(idx)"
              >{{ pageOffset + idx }}</a
            >
          </li>
          <li v-if="page < maxPages - pagePadding - 1" class="disabled">
            <a href="#">...</a>
          </li>
          <li :class="{ disabled: currentPage == maxPages - 1 || !maxPages }">
            <a
              class="pag-icon"
              aria-label="Next"
              @click.stop="currentPage += 1"
              @keyup.enter="currentPage += 1"
            >
              <span aria-hidden="true"><i class="fa fa-forward"></i></span>
            </a>
          </li>
          <li>
            <a
              aria-label="Last"
              @click.stop="currentPage = maxPages - 1"
              @keyup.enter="currentPage = maxPages - 1"
            >
              <span aria-hidden="true">Last</span>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TableControls',
  props: {
    pageSize: {
      type: Number,
      required: true,
    },
    page: {
      type: Number,
      required: true,
    },
    numEntriesFiltered: {
      type: Number,
      required: true,
    },
    numEntries: {
      type: Number,
      required: true,
    },
    pagePadding: {
      type: Number,
      required: false,
      default: 2,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    more: {
      type: Boolean,
      required: true,
    },
    isAnyFilterApplied: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    selPageSize: {
      get() {
        return `${this.pageSize}`;
      },
      set(val) {
        const pageSize = parseInt(val, 10);
        this.$emit('update:pageSize', pageSize);
      },
    },
    currentPage: {
      get() {
        return this.page;
      },
      set(val) {
        if (val >= 0 && val < this.maxPages) {
          this.$emit('update:page', val);
        }
      },
    },
    maxPages() {
      return Math.ceil(this.numEntriesFiltered / this.pageSize);
    },
    pageOffset() {
      if (this.maxPages <= this.numPagesToShow) {
        return 0;
      }
      if (this.page <= this.pagePadding) {
        return 0;
      }
      if (this.page + this.pagePadding >= this.maxPages) {
        return this.maxPages - this.numPagesToShow;
      }
      return this.page - this.pagePadding;
    },
    numPagesToShow() {
      return Math.min(this.pagePadding * 2 + 1, this.maxPages);
    },
    showPagination() {
      return this.numEntriesFiltered > this.pageSize;
    },
  },
  watch: {
    pageSize(val, old) {
      const topOfOld = old * this.currentPage;
      this.currentPage = Math.floor(topOfOld / val);
    },
    maxPages(val) {
      if (val <= this.currentPage) {
        this.currentPage = val - 1;
      }
    },
  },
  methods: {
    pageIdx(idx) {
      return idx - 1 + this.pageOffset;
    },
  },
};
</script>

<style>
.table-ctrls-container {
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 4px 0px;
}

.row-class {
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: center;
}

.size-and-select {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
}

.size-and-select select {
  max-width: 80px;
  min-width: 40px;
}

.row-class p {
  margin: 0;
}

.flex-grid {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0 !important;
  padding: 0px 16px;
}

.pagination-ul {
  margin: 0 !important;
  float: right;
}

.pagination-nav {
}

.pag-icon {
  color: var(--diag-purple4) !important;
}
</style>
